/**
 * Habits.
 *
 * Built against taskdojo-app/src/screens/HabitsScreen.js. What carries over:
 *
 *  - the header -- title, rank badge, the raised add key;
 *  - the TODAY / PROGRESS / ALL-TIME BEST band, in that order and those
 *    colours;
 *  - the card: a 54px icon tile washed in the habit's own colour at 15%, the
 *    title at 16.5px/800 struck through when done, the description, and the
 *    streak as a filled pill rather than grey metadata. The phone's comment on
 *    that is worth repeating: the streak is the reason people come back, so it
 *    reads as something earned, and only lights up once there is a streak to be
 *    proud of;
 *  - the week strip. Seven 14px boxes, oldest left, today outlined in red. A
 *    streak number says how long; this says what actually happened, misses and
 *    all. It is fetched once as a range for every habit rather than per habit
 *    per day.
 *
 * Tapping the card toggles today, as on the phone. Editing is a separate
 * control here rather than a long-press: long-press is a phone gesture with no
 * honest desktop equivalent, and a hidden action on a pointer device is just a
 * missing one.
 *
 * Streak freezes are not ported. They are a Pro feature gated by a subscription
 * the web has no notion of, and a freeze offered but not honoured would cost
 * someone a streak.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useScore } from '../../hooks/useScore';
import BeltBadge from '../common/BeltBadge';
import {
  ScreenHeader, SummaryBand, ActionButton, PrimaryButton, SecondaryButton,
  Field, useInputClass, raised, pressable,
} from '../dojo';
import { dayKey } from '../../utils/score';
import { formatTime } from '../../utils/datetime';
import { readableOn, MARK_MIN } from '../../utils/taskColor';
import {
  toggleHabitCompletion, getHabitCompletionsForDateRange, deleteHabitAndCleanup,
} from '../../services/habitService';
import {
  Habit, createHabit, updateHabit,
} from '../../services/firebaseService';

/** The phone's EMOJI_LIST, verbatim. */
const EMOJIS = ['🔥', '💪', '🎯', '📚', '🏃', '🧘', '💧', '🥗', '😴', '🎨', '✍️', '🎵'];

/**
 * The phone's COLOR_LIST, de-duplicated -- it lists #3E8C84 and #E8B33A twice
 * each, which shows as two pairs of identical swatches in its picker.
 */
const COLORS = ['#E34234', '#3E8C84', '#4A6FA5', '#5FA83A', '#E8B33A', '#8E5572', '#C1763C', '#2F7A8C'];

interface FormState {
  title: string;
  description: string;
  icon: string;
  color: string;
  reminder: string;
}

const emptyForm = (): FormState => ({
  title: '', description: '', icon: EMOJIS[0], color: COLORS[0], reminder: '',
});

/** Local YYYY-MM-DD keys for the last seven days, oldest first. */
const weekKeys = () => {
  const out: string[] = [];
  const start = new Date();
  start.setDate(start.getDate() - 6);
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push(dayKey(d));
  }
  return out;
};

const Habits: React.FC = () => {
  const { state, getTodayCompletions } = useApp();
  const { colors, isDark } = useTheme();
  /** The surface marks and chips sit on, which is what contrast is measured against. */
  const card = isDark ? '#1B2A32' : '#FFFFFF';

  const score = useScore();
  const input = useInputClass();

  const uid = state.user?.uid;
  const habits = useMemo(
    () => [...((state.habits || []) as Habit[])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [state.habits],
  );

  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [week, setWeek] = useState<string[][]>([]);
  const [editing, setEditing] = useState<Habit | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Habit | null>(null);

  const doneToday = getTodayCompletions();
  const isDone = useCallback((id: string) => doneToday.includes(id), [doneToday]);

  const keys = useMemo(weekKeys, []);

  // Re-read the week whenever today's completions change, so ticking a habit
  // updates its strip as well as its check.
  useEffect(() => {
    if (!uid) return;
    let alive = true;
    const start = new Date();
    start.setDate(start.getDate() - 6);
    getHabitCompletionsForDateRange(uid, start, new Date())
      .then((byDate) => {
        if (alive) setWeek(keys.map((k) => byDate?.[k]?.habits || []));
      })
      .catch(() => { if (alive) setWeek(keys.map(() => [])); });
    return () => { alive = false; };
  }, [uid, keys, state.completions]);

  const doneCount = habits.filter((h) => isDone(h.id)).length;
  const rate = habits.length > 0 ? Math.round((doneCount / habits.length) * 100) : 0;
  const bestEver = habits.reduce((m, h) => Math.max(m, h.bestStreak || 0), 0);

  const toggle = async (habit: Habit) => {
    if (!uid || busy[habit.id]) return;
    setBusy((b) => ({ ...b, [habit.id]: true }));
    try {
      // No optimistic write: the completions listener is the source of truth,
      // and guessing here means the tick and the week strip can disagree.
      await toggleHabitCompletion(habit.id, uid);
    } catch (e: any) {
      setError(e?.message || "Couldn't save that. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [habit.id]: false }));
    }
  };

  const openCreate = () => {
    setForm(emptyForm());
    setEditing(null);
    setCreating(true);
    setError('');
  };

  const openEdit = (habit: Habit) => {
    setForm({
      title: habit.title || '',
      description: habit.description || '',
      icon: habit.icon || EMOJIS[0],
      color: habit.color || COLORS[0],
      reminder: habit.reminder ? habit.reminder.slice(11, 16) : '',
    });
    setEditing(habit);
    setCreating(false);
    setError('');
  };

  const closeModal = () => { setEditing(null); setCreating(false); setError(''); };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Give the habit a name.'); return; }
    if (!uid) return;

    setSaving(true);
    setError('');
    try {
      // A reminder is a time of day, but the documents store a full ISO
      // instant, so it is anchored to today and the server reads the hour off
      // it in the user's own zone.
      let reminder: string | null = null;
      if (form.reminder) {
        const [hh, mm] = form.reminder.split(':').map(Number);
        const d = new Date();
        d.setHours(hh || 0, mm || 0, 0, 0);
        reminder = d.toISOString();
      }

      const payload: Partial<Habit> = {
        title: form.title.trim(),
        description: form.description.trim(),
        icon: form.icon,
        color: form.color,
        reminder,
      };

      if (editing) {
        await updateHabit(editing.id, payload);
      } else {
        // The phone writes startTime/endTime as null and orders from 1, so a
        // habit created here is shaped exactly like one created there.
        await createHabit(
          { ...payload, startTime: null, endTime: null, order: habits.length + 1 },
          uid,
        );
      }
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (habit: Habit) => {
    if (!uid) return;
    setSaving(true);
    try {
      // Not the bare deleteHabit: the phone also strips the habit's id out of
      // every completions document and removes any left empty. Deleting only
      // the habit leaves completions pointing at something that no longer
      // exists, which both platforms then read when computing the score.
      await deleteHabitAndCleanup(habit.id, uid);
      setConfirmDelete(null);
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Couldn't delete that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const modalOpen = creating || !!editing;

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title="Habits"
          right={<BeltBadge score={score.score} size={44} />}
          action={
            <ActionButton label="New habit" onClick={openCreate}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </ActionButton>
          }
        />

        <SummaryBand
          items={[
            { value: `${doneCount}/${habits.length}`, label: 'Today' },
            { value: `${rate}%`, label: 'Progress', color: '#4C9A2A' },
            { value: bestEver, label: 'All-time best', color: isDark ? '#FEC200' : '#C08A1E' },
          ]}
        />

        {error && !modalOpen && (
          <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="px-5">
          {habits.length === 0 ? (
            <div className="flex flex-col items-center text-center py-20">
              <div className={`w-[88px] h-[88px] rounded-[30px] border-2 ${colors.cardBorder} ${colors.card}
                flex items-center justify-center mb-[18px] text-[34px]`}>
                🔥
              </div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">No habits yet</h2>
              <p className={`text-[15px] font-semibold mb-5 ${colors.textSecondary}`}>
                Start with one you can do today.
              </p>
              <button
                type="button"
                onClick={openCreate}
                className={`rounded-[14px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
                  px-5 pt-[13px] pb-[11px] text-[15px] font-extrabold`}
              >
                Create your first habit
              </button>
            </div>
          ) : (
            <ul className="space-y-3 list-none p-0 m-0">
              {habits.map((habit) => {
                const done = isDone(habit.id);
                const working = !!busy[habit.id];
                const streak = habit.streak || 0;

                return (
                  <li key={habit.id}>
                    <div
                      className={`rounded-[20px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
                        px-4 pt-4 pb-1.5 transition-opacity ${working ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-start gap-3.5">
                        {/* The habit's colour, washed, so each card is
                            identifiable without any of them shouting. */}
                        <span
                          className="w-[54px] h-[54px] rounded-[18px] flex items-center justify-center
                            text-[28px] leading-none flex-shrink-0"
                          style={{ backgroundColor: `${habit.color}26` }}
                        >
                          {habit.icon || '🎯'}
                        </span>

                        <div className="flex-1 min-w-0">
                          <h3 className={`text-[16.5px] font-extrabold tracking-[-0.2px] break-words
                            ${done ? 'line-through opacity-60' : ''}`}>
                            {habit.title}
                          </h3>
                          {habit.description && (
                            <p className={`text-[13px] mt-0.5 ${colors.textSecondary}`}>
                              {habit.description}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            <span
                              className={`px-[9px] py-1 rounded-full text-[12.5px] font-extrabold tabular-nums
                                ${streak > 0
                                  ? 'bg-dojo-red text-white'
                                  : `${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-100'} ${colors.textSecondary}`}`}
                            >
                              {streak} day streak
                            </span>

                            {habit.reminder && (
                              <span className="flex items-center gap-1 text-[12px] font-semibold text-dojo-red">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                  stroke="currentColor" strokeWidth="2.5">
                                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
                                    strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Daily at {formatTime(habit.reminder)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => openEdit(habit)}
                            aria-label={`Edit ${habit.title}`}
                            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors
                              ${colors.textMuted} ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'}`}
                          >
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="2.2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z"
                                strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() => toggle(habit)}
                            disabled={working}
                            aria-pressed={done}
                            aria-label={done ? `Mark ${habit.title} not done today` : `Mark ${habit.title} done today`}
                            className="w-11 h-11 flex items-center justify-center disabled:opacity-50"
                          >
                            {done ? (
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="#E34234">
                                <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.2l-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4z" />
                              </svg>
                            ) : (
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="1.8" className={colors.textMuted}>
                                <circle cx="12" cy="12" r="10" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* The last seven days, oldest left, on their own line so
                          nothing can collide with the check control. */}
                      {week.length === 7 && (
                        <div className={`flex items-center justify-between border-t ${colors.border}
                          mt-3 pt-2.5 pb-1`}>
                          <div className="flex gap-1">
                            {week.map((ids, i) => {
                              const on = ids.includes(habit.id);
                              const isToday = i === week.length - 1;
                              return (
                                <span
                                  key={keys[i]}
                                  title={keys[i]}
                                  className="w-[14px] h-[14px] rounded-[4px] block"
                                  style={{
                                    backgroundColor: on
                                      ? readableOn(habit.color, card, MARK_MIN)
                                      : (isDark ? '#152028' : '#F0EBE1'),
                                    border: isToday
                                      ? '2px solid #E34234'
                                      : `1px solid ${on ? habit.color : (isDark ? '#334155' : '#D6D3D1')}`,
                                  }}
                                />
                              );
                            })}
                          </div>
                          <span className={`text-[11px] font-bold uppercase tracking-[0.5px] ${colors.textSecondary}`}>
                            last 7 days
                          </span>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* --------------------------- the editor --------------------------- */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
          onClick={closeModal}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-lg max-h-[92vh] overflow-y-auto ${colors.card}
              border-2 ${colors.cardBorder} rounded-t-[22px] sm:rounded-[22px] p-6`}
          >
            <div className="flex items-center gap-3 mb-5">
              <h2 className="text-[26px] font-black tracking-[-0.7px] flex-1">
                {editing ? 'Edit habit' : 'New habit'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close"
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.textSecondary}
                  ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <Field label="Name" className="mb-4">
              <input
                className={input}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Drink water"
                autoFocus
              />
            </Field>

            <Field label="Description" className="mb-4">
              <input
                className={input}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="optional"
              />
            </Field>

            <Field label="Icon" className="mb-4">
              <div className="flex flex-wrap gap-2">
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setForm({ ...form, icon: e })}
                    aria-pressed={form.icon === e}
                    className={`w-11 h-11 rounded-[14px] border-2 text-[20px] flex items-center justify-center
                      transition-colors
                      ${form.icon === e
                        ? 'border-dojo-red bg-dojo-red/10'
                        : `${colors.cardBorder} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Colour" className="mb-4">
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    aria-label={c}
                    aria-pressed={form.color === c}
                    className={`w-9 h-9 rounded-xl border-2 transition-transform
                      ${form.color === c ? 'scale-110 border-current' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </Field>

            <Field label="Daily reminder" className="mb-5">
              <input
                type="time"
                className={input}
                value={form.reminder}
                onChange={(e) => setForm({ ...form, reminder: e.target.value })}
              />
              <p className={`text-[12px] font-semibold mt-1.5 ${colors.textSecondary}`}>
                Notifications are sent by the taskDojo app, not the browser.
              </p>
            </Field>

            {error && (
              <div role="alert" className="mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
                px-4 py-3 text-[13px] font-semibold text-dojo-red">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <PrimaryButton type="submit" disabled={saving} className="flex-1">
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create habit'}
              </PrimaryButton>
              {editing && (
                <SecondaryButton
                  onClick={() => setConfirmDelete(editing)}
                  disabled={saving}
                  className="!text-dojo-red"
                >
                  Delete
                </SecondaryButton>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Deleting takes the streak with it, so it asks. */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Delete this habit?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              “{confirmDelete.title}” and its {confirmDelete.streak || 0}-day streak will be removed.
              This can't be undone.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmDelete(null)} className="flex-1">
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={() => remove(confirmDelete)} disabled={saving} className="flex-1">
                {saving ? 'Deleting…' : 'Delete'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Habits;
